import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import {
  getTerceros, descargarTercerosExport,
  getTercerosPorSucursal, descargarTercerosPorSucursalExport,
} from "./api"
import { rangoDelMes } from "./helpers"
import HeaderFiltros from "./HeaderFiltros"
import EditarOverrideModal from "./EditarOverrideModal"
import WizardLimpieza from "./WizardLimpieza"
import ResumenTab from "./tabs/ResumenTab"
import TercerosTab from "./tabs/TercerosTab"
import ImpuestosTab from "./tabs/ImpuestosTab"
import PorSucursalTab from "./tabs/PorSucursalTab"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Comprobantes", link: "#" },
  { title: "Análisis de Terceros", link: "#" },
]

const TABS = [
  { id: "resumen", label: "Resumen", icon: "mdi-view-dashboard-outline" },
  { id: "terceros", label: "Terceros", icon: "mdi-account-group" },
  { id: "impuestos", label: "Impuestos", icon: "mdi-bank" },
  { id: "por-sucursal", label: "Por sucursal", icon: "mdi-store-outline" },
]

const ahora = new Date()

const Terceros = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  // Filtros
  const [modo, setModo] = useState("mes")
  const [year, setYear] = useState(ahora.getFullYear())
  const [month, setMonth] = useState(ahora.getMonth() + 1)
  const [fechaInicio, setFechaInicio] = useState("")
  const [fechaFin, setFechaFin] = useState("")
  const [campoFecha, setCampoFecha] = useState("fecha")
  const [sucursal, setSucursal] = useState("")
  const [tipoComprobante, setTipoComprobante] = useState("")
  const [excluirCF, setExcluirCF] = useState(false)

  // Estado data
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [tabActivo, setTabActivo] = useState("resumen")

  // Export
  const [exportando, setExportando] = useState(null)
  const [exportInfo, setExportInfo] = useState(null)

  // Modal de edición de canónico (clic puntual en una fila)
  const [modalAbierto, setModalAbierto] = useState(false)
  const [terceroEditando, setTerceroEditando] = useState(null)

  // Wizard "Modo limpieza" — recorre todos los terceros con variantes
  const [wizardAbierto, setWizardAbierto] = useState(false)

  const [filtrosAplicados, setFiltrosAplicados] = useState(null)

  // ───── Estado del tab "Por sucursal" (independiente) ─────
  const [dataPorSucursal, setDataPorSucursal] = useState(null)
  const [loadingPorSucursal, setLoadingPorSucursal] = useState(false)
  const [errorPorSucursal, setErrorPorSucursal] = useState("")
  const [limitPorSucursal, setLimitPorSucursal] = useState(50)
  const [filtrosPorSucursalAplicados, setFiltrosPorSucursalAplicados] = useState(null)

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""
  const userEmail = user?.email || user?.correo || user?.username || "portal-web"

  useEffect(() => {
    document.title = "Análisis de Terceros | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Análisis de Terceros"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  // Reset al cambiar empresa
  useEffect(() => {
    setData(null)
    setError("")
    setFiltrosAplicados(null)
    setTabActivo("resumen")
    setSucursal("")
    setTipoComprobante("")
    setDataPorSucursal(null)
    setErrorPorSucursal("")
    setFiltrosPorSucursalAplicados(null)
  }, [nitemisor])

  // Catálogo de sucursales del último response (para HeaderFiltros)
  // El back solo permite filtrar por UNA sucursal, así que usamos un select.
  const sucursalesDisponibles = useMemo(() => {
    // El response no trae lista explícita de sucursales — la inferimos
    // de los terceros (cada uno tiene sucursales_atendidas pero no sus nombres
    // a menos que esté en el desglose). Como el back no expone catálogo aún,
    // dejamos solo lo que aparezca en futuras respuestas.
    return []
  }, [data])

  const puedeConsultar = useMemo(() => {
    if (!nitemisor) return false
    if (modo === "mes") return Boolean(year && month)
    return Boolean(fechaInicio && fechaFin && fechaInicio <= fechaFin)
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin])

  const construirFiltros = useCallback(() => {
    const base = {
      nitemisor,
      campo_fecha: campoFecha,
      sucursal: sucursal || undefined,
      tipo_comprobante: tipoComprobante || undefined,
      excluir_consumidor_final: excluirCF,
    }
    if (modo === "mes") {
      return { ...base, year, mes: month }
    }
    return { ...base, fecha_inicio: fechaInicio, fecha_fin: fechaFin }
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin, campoFecha, sucursal, tipoComprobante, excluirCF])

  const cargar = useCallback(async opts => {
    setLoading(true)
    setError("")
    try {
      const response = await getTerceros(opts)
      const payload = response.data
      if (payload?.success === false) {
        throw new Error(payload?.message || payload?.error || "El servidor reportó un error.")
      }
      setData(payload)
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || "No se pudo cargar el análisis.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleConsultar = () => {
    const opts = construirFiltros()
    setFiltrosAplicados(opts)
    cargar(opts)
  }

  // ───── Carga del tab "Por sucursal" ─────
  const cargarPorSucursal = useCallback(async (opts) => {
    setLoadingPorSucursal(true)
    setErrorPorSucursal("")
    try {
      const response = await getTercerosPorSucursal(opts)
      const payload = response.data
      if (payload?.success === false) {
        throw new Error(payload?.message || payload?.error || "El servidor reportó un error.")
      }
      setDataPorSucursal(payload)
    } catch (err) {
      setErrorPorSucursal(err.response?.data?.error || err.response?.data?.message || err.message || "No se pudo cargar el análisis por sucursal.")
      setDataPorSucursal(null)
    } finally {
      setLoadingPorSucursal(false)
    }
  }, [])

  const construirFiltrosPorSucursal = useCallback(() => {
    const base = construirFiltros()
    delete base.limit
    delete base.offset
    return { ...base, limit_por_sucursal: limitPorSucursal }
  }, [construirFiltros, limitPorSucursal])

  const handleConsultarPorSucursal = () => {
    if (!puedeConsultar) return
    const opts = construirFiltrosPorSucursal()
    setFiltrosPorSucursalAplicados(opts)
    cargarPorSucursal(opts)
  }

  const handleRecargarPorSucursal = () => {
    if (filtrosPorSucursalAplicados) cargarPorSucursal(filtrosPorSucursalAplicados)
    else handleConsultarPorSucursal()
  }

  const handleExportarPorSucursal = async () => {
    if (exportando) return
    setExportando("xlsx-sucursal")
    setExportInfo(null)
    setError("")
    try {
      let opts
      if (modo === "mes") {
        const { fecha_inicio, fecha_fin } = rangoDelMes(year, month)
        opts = {
          nitemisor, fecha_inicio, fecha_fin,
          campo_fecha: campoFecha,
          sucursal: sucursal || undefined,
          tipo_comprobante: tipoComprobante || undefined,
          excluir_consumidor_final: excluirCF,
        }
      } else {
        opts = {
          nitemisor, fecha_inicio: fechaInicio, fecha_fin: fechaFin,
          campo_fecha: campoFecha,
          sucursal: sucursal || undefined,
          tipo_comprobante: tipoComprobante || undefined,
          excluir_consumidor_final: excluirCF,
        }
      }
      const info = await descargarTercerosPorSucursalExport(opts)
      setExportInfo({ formato: "xlsx-sucursal", totalRows: info.totalRows, totalSheets: info.totalSheets, filename: info.filename })
      setTimeout(() => setExportInfo(null), 8000)
    } catch (err) {
      setErrorPorSucursal(err.response?.data?.error || err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  // Recargar tras editar override (el back invalida cache automáticamente)
  const handleCambioGuardado = () => {
    if (filtrosAplicados) cargar(filtrosAplicados)
  }

  // Export: usa el back (CSV o XLSX)
  const handleExportar = async (formato) => {
    if (!filtrosAplicados || exportando) return
    setExportando(formato)
    setExportInfo(null)
    setError("")
    try {
      let opts
      if (modo === "mes") {
        const { fecha_inicio, fecha_fin } = rangoDelMes(year, month)
        opts = {
          nitemisor, fecha_inicio, fecha_fin,
          campo_fecha: campoFecha,
          sucursal: sucursal || undefined,
          tipo_comprobante: tipoComprobante || undefined,
          excluir_consumidor_final: excluirCF,
          formato,
        }
      } else {
        opts = {
          nitemisor, fecha_inicio: fechaInicio, fecha_fin: fechaFin,
          campo_fecha: campoFecha,
          sucursal: sucursal || undefined,
          tipo_comprobante: tipoComprobante || undefined,
          excluir_consumidor_final: excluirCF,
          formato,
        }
      }
      const info = await descargarTercerosExport(opts)
      setExportInfo({ formato, totalRows: info.totalRows, filename: info.filename })
      setTimeout(() => setExportInfo(null), 6000)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  const abrirModalEditar = (tercero) => {
    setTerceroEditando(tercero)
    setModalAbierto(true)
  }

  // Calculados antes del early-return para no violar reglas de hooks
  const tercerosConVariantes = useMemo(
    () => (data?.terceros || []).filter(t => t.tiene_variantes),
    [data],
  )
  // Pendientes de validar = tienen variantes Y aún no tienen override manual
  const tercerosPendientesWizard = useMemo(
    () => tercerosConVariantes.filter(t => t.nombre_canonico_origen !== "manual"),
    [tercerosConVariantes],
  )
  const variantesPendientes = tercerosPendientesWizard.length

  if (!nitemisor) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px dashed #cbd5e1" }}>
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> en la parte superior derecha.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Comprobantes
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Análisis de Terceros
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{empresaNombre}</strong> · NIT {nitemisor}
              {data?.duration && (
                <>
                  {" · "}
                  <i className="mdi mdi-clock-outline me-1" />
                  {data.duration}
                </>
              )}
            </div>
          </div>
          {data && (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {variantesPendientes > 0 && (
                <button
                  type="button"
                  className="btn btn-warning btn-sm"
                  onClick={() => setWizardAbierto(true)}
                  title="Revisar uno por uno los terceros con variantes de nombre"
                >
                  <i className="mdi mdi-broom me-1" />
                  Modo limpieza ({variantesPendientes})
                </button>
              )}
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={() => handleExportar("xlsx")}
                  disabled={!!exportando}
                  title="Descargar Excel del back (con formato profesional)"
                >
                  {exportando === "xlsx" ? (
                    <><Spinner size="sm" className="me-1" /> Generando…</>
                  ) : (
                    <><i className="mdi mdi-file-excel me-1" /> Excel</>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => handleExportar("csv")}
                  disabled={!!exportando}
                  title="Descargar CSV en streaming"
                >
                  {exportando === "csv" ? (
                    <><Spinner size="sm" className="me-1" /> Generando…</>
                  ) : (
                    <><i className="mdi mdi-file-delimited me-1" /> CSV</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <HeaderFiltros
          modo={modo} setModo={setModo}
          year={year} setYear={setYear} month={month} setMonth={setMonth}
          fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
          fechaFin={fechaFin} setFechaFin={setFechaFin}
          campoFecha={campoFecha} setCampoFecha={setCampoFecha}
          sucursal={sucursal} setSucursal={setSucursal}
          tipoComprobante={tipoComprobante} setTipoComprobante={setTipoComprobante}
          excluirCF={excluirCF} setExcluirCF={setExcluirCF}
          sucursalesDisponibles={sucursalesDisponibles}
          loading={loading}
          onConsultar={handleConsultar}
          puedeConsultar={puedeConsultar}
        />

        {exportInfo && (
          <div style={{
            marginTop: 12,
            background: "#d1fae5", border: "1px solid #a7f3d0",
            color: "#065f46", padding: "10px 14px", borderRadius: 8,
            fontSize: 13, display: "flex", alignItems: "center", gap: 10,
          }}>
            <i className="mdi mdi-check-circle" style={{ fontSize: 18 }} />
            <span>
              Descarga lista{exportInfo.totalRows ? `: ${exportInfo.totalRows.toLocaleString("es-CO")} filas` : ""}
              {exportInfo.filename ? ` · ${exportInfo.filename}` : ""}
            </span>
            <button
              type="button"
              className="btn-close ms-auto"
              style={{ fontSize: 10 }}
              onClick={() => setExportInfo(null)}
            />
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca",
          color: "#991b1b", borderRadius: 10,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ms-auto"
            onClick={() => filtrosAplicados && cargar(filtrosAplicados)}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Loading */}
      {!data && !dataPorSucursal && loading && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando análisis…</div>
        </div>
      )}

      {/* Empty state */}
      {!data && !dataPorSucursal && !loading && !error && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "60px 40px", textAlign: "center", border: "1px dashed #cbd5e1" }}>
          <i className="mdi mdi-account-search-outline" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Selecciona el período y consulta
          </h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Indica mes/año o rango de fechas y pulsa <strong>Consultar</strong> para ver el análisis
            de terceros de <strong>{empresaNombre}</strong>. También podés ir directo a
            <strong> Por sucursal</strong> si solo te interesa esa vista.
          </p>
        </div>
      )}

      {/* Tabs */}
      {(data || dataPorSucursal) && (
        <>
          <div style={{
            background: "#fff", borderRadius: "14px 14px 0 0",
            border: "1px solid #e2e8f0", borderBottom: "none",
            padding: "0 12px",
            display: "flex", gap: 4, overflowX: "auto",
          }}>
            {TABS.map(tab => {
              const activo = tabActivo === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTabActivo(tab.id)}
                  style={{
                    background: "none", border: "none",
                    padding: "14px 18px",
                    fontSize: 13, fontWeight: 600,
                    color: activo ? "#0f172a" : "#64748b",
                    borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                    cursor: "pointer", transition: "color 0.15s",
                    display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                  }}
                >
                  <i className={`mdi ${tab.icon}`} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          <div>
            {tabActivo !== "por-sucursal" && !data && (
              <div style={{ background: "#fff", border: "1px dashed #cbd5e1", borderRadius: "0 0 14px 14px", padding: "50px 30px", textAlign: "center" }}>
                <i className="mdi mdi-information-outline" style={{ fontSize: 44, color: "#cbd5e1" }} />
                <h6 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
                  Esta vista requiere el análisis detallado
                </h6>
                <p style={{ color: "#64748b", maxWidth: 420, margin: "6px auto 14px", fontSize: 13 }}>
                  Pulsá <strong>Consultar</strong> arriba para cargar el análisis principal.
                </p>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleConsultar}
                  disabled={!puedeConsultar || loading}
                >
                  {loading ? <><Spinner size="sm" className="me-1" /> Cargando…</> : <><i className="mdi mdi-magnify me-1" /> Consultar análisis principal</>}
                </button>
              </div>
            )}
            {tabActivo === "resumen" && data && (
              <ResumenTab
                data={data}
                onAbrirWizard={() => setWizardAbierto(true)}
              />
            )}
            {tabActivo === "terceros" && data && <TercerosTab data={data} onEditarCanonico={abrirModalEditar} />}
            {tabActivo === "impuestos" && data && <ImpuestosTab data={data} />}
            {tabActivo === "por-sucursal" && (
              <PorSucursalTab
                data={dataPorSucursal}
                loading={loadingPorSucursal}
                error={errorPorSucursal}
                onConsultar={handleConsultarPorSucursal}
                onRecargar={handleRecargarPorSucursal}
                onExportar={handleExportarPorSucursal}
                exportando={exportando}
                exportInfo={exportInfo?.formato === "xlsx-sucursal" ? exportInfo : null}
                limitPorSucursal={limitPorSucursal}
                setLimitPorSucursal={setLimitPorSucursal}
                listo={puedeConsultar}
                totalVentasDetallado={Number(data?.metadata?.total_ventas_global) || undefined}
              />
            )}
          </div>
        </>
      )}

      {/* Modal de edición puntual (clic en una fila) */}
      <EditarOverrideModal
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
        tercero={terceroEditando}
        nitemisor={nitemisor}
        actualizadoPor={userEmail}
        onCambioGuardado={handleCambioGuardado}
      />

      {/* Wizard "Modo limpieza" — recorre solo los pendientes (sin override manual) */}
      <WizardLimpieza
        abierto={wizardAbierto}
        onCerrar={() => setWizardAbierto(false)}
        terceros={tercerosPendientesWizard}
        nitemisor={nitemisor}
        actualizadoPor={userEmail}
        onCambioGuardado={handleCambioGuardado}
      />
    </div>
  )
}

Terceros.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(Terceros)
